
import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc } from 'firebase/firestore';
import { UserRole } from '../types';

interface LeaveRequest {
    id: string;
    requesterId: string;
    requesterName: string;
    requesterRole: UserRole;
    startDate: string;
    endDate: string;
    reason: string;
    status: 'pending' | 'approved' | 'rejected';
    processedBy?: string;
    createdAt: number;
}

const LeaveManagement: React.FC<{ user: any }> = ({ user }) => {
    const [leaves, setLeaves] = useState<LeaveRequest[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({ startDate: '', endDate: '', reason: '' });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let q;
        if (user.role === UserRole.STUDENT) {
            q = query(collection(db, 'leaves'), where('requesterId', '==', user.uid));
        } else {
            q = collection(db, 'leaves');
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as LeaveRequest));
            
            // Client-side sort to fix index requirement error
            data.sort((a, b) => b.createdAt - a.createdAt);

            if (user.role === UserRole.STAFF) {
                // Staff see their own leaves AND leaves from students
                setLeaves(data.filter(l => l.requesterId === user.uid || l.requesterRole === UserRole.STUDENT));
            } else {
                setLeaves(data);
            }
        });
        return unsubscribe;
    }, [user]);

    const handleRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await addDoc(collection(db, 'leaves'), {
                requesterId: user.uid,
                requesterName: user.displayName,
                requesterRole: user.role,
                ...formData,
                status: 'pending',
                createdAt: Date.now()
            });
            setIsModalOpen(false);
            setFormData({ startDate: '', endDate: '', reason: '' });
        } catch (err) {
            alert("Error: " + err);
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (id: string, status: 'approved' | 'rejected') => {
        await updateDoc(doc(db, 'leaves', id), { status, processedBy: user.displayName });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'approved': return 'bg-green-100 text-green-600 border-green-200';
            case 'rejected': return 'bg-red-100 text-red-600 border-red-200';
            default: return 'bg-orange-100 text-orange-600 border-orange-200';
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold dark:text-white">Leave Requests</h2>
                <button onClick={() => setIsModalOpen(true)} className="px-5 py-2.5 bg-purple-600 text-white rounded-xl font-bold shadow-lg hover:bg-purple-700 transition-all flex items-center gap-2">
                    <i className="fas fa-paper-plane"></i> Apply for Leave
                </button>
            </div>

            <div className="grid gap-4">
                {leaves.map(leaf => (
                    <div key={leaf.id} className="bg-white dark:bg-dark-card p-6 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-gray-50 dark:bg-gray-800 flex items-center justify-center text-gray-400">
                                <i className="fas fa-calendar-alt text-xl"></i>
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded border ${getStatusColor(leaf.status)}`}>
                                        {leaf.status}
                                    </span>
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{leaf.requesterRole}</span>
                                </div>
                                <h4 className="font-bold dark:text-white">{leaf.requesterName}</h4>
                                <p className="text-xs text-gray-500 mt-1 line-clamp-1"><span className="font-semibold text-purple-500">Reason:</span> {leaf.reason}</p>
                                <p className="text-[10px] text-gray-400 mt-2">
                                    <i className="fas fa-clock mr-1"></i> {leaf.startDate} to {leaf.endDate}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                            {leaf.status === 'pending' && (
                                <>
                                    {((user.role === UserRole.STAFF && leaf.requesterRole === UserRole.STUDENT) || user.role === UserRole.ADMIN) && (
                                        <>
                                            <button onClick={() => handleAction(leaf.id, 'approved')} className="p-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all shadow-md">
                                                <i className="fas fa-check"></i>
                                            </button>
                                            <button onClick={() => handleAction(leaf.id, 'rejected')} className="p-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all shadow-md">
                                                <i className="fas fa-times"></i>
                                            </button>
                                        </>
                                    )}
                                </>
                            )}
                            {leaf.status !== 'pending' && (
                                <p className="text-[10px] font-bold text-gray-400 italic">Processed by {leaf.processedBy || 'Manager'}</p>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white dark:bg-dark-card w-full max-w-md rounded-[2.5rem] p-8 shadow-2xl relative border border-gray-100 dark:border-gray-800">
                        <h3 className="text-xl font-black dark:text-white mb-6 flex items-center gap-3">
                            <i className="fas fa-file-signature text-purple-500"></i> New Leave Application
                        </h3>
                        <form onSubmit={handleRequest} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">From</label>
                                    <input required type="date" className="w-full bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3 text-sm dark:text-white outline-none border-none focus:ring-2 focus:ring-purple-500" value={formData.startDate} onChange={e => setFormData({...formData, startDate: e.target.value})} />
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">To</label>
                                    <input required type="date" className="w-full bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3 text-sm dark:text-white outline-none border-none focus:ring-2 focus:ring-purple-500" value={formData.endDate} onChange={e => setFormData({...formData, endDate: e.target.value})} />
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Reason / Description</label>
                                <textarea required placeholder="Briefly explain the reason for leave..." className="w-full bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3 text-sm dark:text-white outline-none border-none focus:ring-2 focus:ring-purple-500 min-h-[120px]" value={formData.reason} onChange={e => setFormData({...formData, reason: e.target.value})} />
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-4 text-sm font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-2xl transition-all">Cancel</button>
                                <button type="submit" disabled={loading} className="flex-1 py-4 bg-purple-600 text-white font-bold rounded-2xl shadow-xl hover:bg-purple-700 transition-all flex items-center justify-center">
                                    {loading ? <i className="fas fa-spinner animate-spin"></i> : 'Submit Application'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeaveManagement;
